import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Chip,
  ButtonGroup,
  Collapse,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { SearchInput } from 'components/SearchInput';
import useUrl from 'hooks/useUrl';
import { useTablePagination } from 'hooks/useTablePagination';
import MembersV2Table from './MembersV2Table/MembersV2Table';
import type {
  MemberSearchItem,
  SearchMembersByFieldsPayload,
  MemberSearchSortBy,
  MemberSearchSortOrder,
  MemberStatus,
} from 'TeamModule/api/searchMembersByFieldsApi';
import { useSearchMembersByFieldsQuery } from 'TeamModule/query/hooks/useSearchMembersByFieldsQuery';
import { useMembersImpersonationQuery } from 'TeamModule/query/hooks/useMembersImpersonationQuery';
import { arrayToCsv } from 'utils/query';
import { ORDER_DIRECTION } from '@constants';
import { TenantSelector } from 'TenantModule/components/TenantSelector';
import RealmSelector from 'SandboxModule/components/RealmSelector';
import { useTenantDropdown } from 'TenantModule/hooks/useTenantDropdown';
import { useTenantRealmDropdownsFlow } from 'TenantModule/hooks/useTenantRealmDropdownsFlow';
import { useTenants } from 'TenantModule/hooks/useTenants';
import { useRef } from 'react';
import StandardPageHeader from 'components/StandardPageHeader';
import { PageContainer, MinimalCard, CleanButton } from 'components/styled';
import {
  Search,
  Filter,
  X,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Building2,
  Tag,
  Users,
} from 'lucide-react';

// Shared constants/types used inside hooks; hoist to module scope to satisfy exhaustive-deps
const NUMERIC_ID_REGEX = /^\d+$/;
type FiltersState = {
  tenant: string;
  realmId: string;
  teamIds: string[];
  teamIdsMode: 'include' | 'exclude';
  statuses: MemberStatus[];
};
const defaultFilters: FiltersState = {
  tenant: '',
  realmId: '',
  teamIds: [],
  teamIdsMode: 'include',
  statuses: ['active'],
};

// Helper functions hoisted to module scope to avoid "used before declaration" issues
const toSortBy = (col: string): MemberSearchSortBy => {
  const allowed: MemberSearchSortBy[] = [
    'teamId',
    'email',
    'firstName',
    'lastName',
    'tenant',
    'realmId',
    'lastBackfilled',
  ];
  return (allowed as string[]).includes(col) ? (col as MemberSearchSortBy) : 'lastBackfilled';
};

const toSortOrder = (
  dir: (typeof ORDER_DIRECTION)[keyof typeof ORDER_DIRECTION],
): MemberSearchSortOrder => (dir === ORDER_DIRECTION.ASC ? 'ASC' : 'DESC');

export const MembersPage = () => {
  const { queryParams, pushQueryParams } = useUrl();
  const { pageNumber, handlePageChange, limit } = useTablePagination(50, 1, 1);

  // Data fetch
  const [orderBy, setOrderBy] = useState<string>('lastBackfilled');
  const [orderDirection, setOrderDirection] = useState<
    (typeof ORDER_DIRECTION)[keyof typeof ORDER_DIRECTION]
  >(ORDER_DIRECTION.DESC);

  /* ------------------------------- Filters UI ------------------------------ */
  const [filters, setFilters] = useState<FiltersState>(defaultFilters);
  const [teamIdInput, setTeamIdInput] = useState<string>('');
  const [submittedPayload, setSubmittedPayload] = useState<SearchMembersByFieldsPayload | null>(
    null,
  );
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [searchInput, setSearchInput] = useState<string>(queryParams.get('q') || '');

  // Selection state - declared early because callbacks reference setSelectedIdsState
  const [selectedIdsState, setSelectedIdsState] = useState<Set<string>>(new Set());

  const handleAddTeamIdChip = useCallback(() => {
    const v = teamIdInput.trim();
    if (!v) return;
    if (!NUMERIC_ID_REGEX.test(v)) return;
    setFilters((prev) => ({ ...prev, teamIds: Array.from(new Set([...(prev.teamIds || []), v])) }));
    setTeamIdInput('');
  }, [teamIdInput]);
  const removeTeamIdChip = (id: string) =>
    setFilters((prev) => ({ ...prev, teamIds: (prev.teamIds || []).filter((x) => x !== id) }));

  const onPageChange = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
      setSelectedIdsState(new Set());
      handlePageChange({ newPageNumber: newPage });
      setSubmittedPayload((prev: SearchMembersByFieldsPayload | null) => ({
        ...(prev || {}),
        page: newPage,
      }));
    },
    [handlePageChange],
  );

  // paginationProps depends on total; move after data fetch

  const onSort = useCallback(
    (columnId: string) => {
      if (orderBy === columnId) {
        const nextDir =
          orderDirection === ORDER_DIRECTION.ASC ? ORDER_DIRECTION.DESC : ORDER_DIRECTION.ASC;
        setOrderDirection(nextDir);
        setSubmittedPayload((prev: SearchMembersByFieldsPayload | null) => ({
          ...(prev || {}),
          sortBy: toSortBy(columnId),
          sortOrder: toSortOrder(nextDir),
        }));
      } else {
        const nextDir = ORDER_DIRECTION.DESC;
        setOrderBy(columnId);
        setOrderDirection(nextDir);
        setSubmittedPayload((prev: SearchMembersByFieldsPayload | null) => ({
          ...(prev || {}),
          sortBy: toSortBy(columnId),
          sortOrder: toSortOrder(nextDir),
        }));
      }
    },
    [orderBy, orderDirection],
  );

  const onReset = useCallback(() => {
    setFilters(defaultFilters);
    setTeamIdInput('');
    setSearchInput('');
    setSubmittedPayload(null);
    // Clear global text search param `q` from the URL
    const entriesObj: Record<string, string> = Object.fromEntries(queryParams.entries());
    const { q: _omit, ...rest } = entriesObj;
    pushQueryParams(rest);
  }, [pushQueryParams, queryParams]);

  // Build payloads and params
  // Note: `|| undefined` converts falsy values (null, "", undefined) to undefined
  // for cleaner API payloads. Empty strings are intentionally excluded.
  const buildBaseFromFilters = useCallback(
    (f: FiltersState): Partial<SearchMembersByFieldsPayload> => {
      const base: Partial<SearchMembersByFieldsPayload> = {
        tenant: f.tenant || undefined,
        realmId: f.realmId || undefined,
        ...(f.teamIds && f.teamIds.length > 0
          ? f.teamIdsMode === 'include'
            ? { teamIds: f.teamIds }
            : { teamIdsNot: f.teamIds }
          : {}),
      };

      // Translate UI status selections to backend params.
      // Backend uses: includeInactive (bool), teamStatus (single enum), isArchived (bool).
      // Default behavior (no params): returns active, non-archived members only.
      if (f.statuses.length > 0) {
        const hasActive = f.statuses.includes('active');
        const hasDeactivated = f.statuses.includes('deactivated');
        const hasDeleted = f.statuses.includes('deleted');

        if (hasActive && hasDeactivated && hasDeleted) {
          // All selected — show everything
          base.includeInactive = true;
        } else if (hasActive && !hasDeactivated && !hasDeleted) {
          // Only active — default behavior, no extra params needed
        } else if (!hasActive && hasDeactivated && !hasDeleted) {
          // Only deactivated
          base.includeInactive = true;
          base.teamStatus = 'deactivated';
        } else if (!hasActive && !hasDeactivated && hasDeleted) {
          // Only deleted
          base.includeInactive = true;
          base.teamStatus = 'deleted';
        } else if (hasActive && hasDeactivated && !hasDeleted) {
          // Active + Deactivated — exclude deleted
          base.includeInactive = true;
          base.teamStatus = 'active';
        } else if (hasActive && !hasDeactivated && hasDeleted) {
          // Active + Deleted — non-archived members
          base.includeInactive = true;
          base.isArchived = false;
        } else if (!hasActive && hasDeactivated && hasDeleted) {
          // Deactivated + Deleted — show all inactive
          base.includeInactive = true;
        }
      }

      return base;
    },
    [],
  );

  const buildPayload = useCallback((): SearchMembersByFieldsPayload => {
    const base = buildBaseFromFilters(filters);
    const payload: SearchMembersByFieldsPayload = {
      ...base,
      page: pageNumber,
      limit,
      sortBy: toSortBy(orderBy),
      sortOrder: toSortOrder(orderDirection),
    };
    return payload;
  }, [buildBaseFromFilters, filters, limit, orderBy, orderDirection, pageNumber]);

  const buildApiParamsFromPayload = useCallback(
    (payload: SearchMembersByFieldsPayload | null): Record<string, string | number | undefined> => {
      const p = payload || ({} as SearchMembersByFieldsPayload);
      const {
        tenant,
        realmId,
        teamId,
        teamIds,
        teamIdsNot,
        text,
        includeInactive,
        teamStatus,
        isArchived,
        page = 1,
        limit = 50,
        sortBy = 'lastBackfilled',
        sortOrder = 'DESC',
      } = p;
      const q: Record<string, string | number | undefined> = {};
      if (tenant) q.tenant = tenant;
      if (realmId) q.realmId = realmId;
      if (teamId) q.teamId = teamId;
      if (teamIds && teamIds.length) q.teamIds = arrayToCsv(teamIds);
      if (teamIdsNot && teamIdsNot.length) q.teamIdsNot = arrayToCsv(teamIdsNot);
      if (text) q.text = text;
      if (includeInactive) q.includeInactive = 'true';
      if (teamStatus) q.teamStatus = teamStatus;
      if (isArchived !== undefined) q.isArchived = String(isArchived);
      q.page = page;
      q.limit = limit;
      q.sortBy = sortBy;
      q.sortOrder = sortOrder;
      return q;
    },
    [],
  );

  // Initialize submittedPayload: prioritize URL `q` param, otherwise use default payload.
  // This logic is consolidated into a single effect to avoid a subtle race condition:
  // Previously, if two separate effects ran on mount, both could see submittedPayload as null.
  // As a result, both effects might call setSubmittedPayload—one with the URL-based payload,
  // and one with the default payload. If the default payload effect runs last, it overwrites
  // the URL-based search, causing the intended search to be lost. By combining the logic here,
  // we ensure that the URL-based search always takes precedence and is not accidentally overwritten.
  useEffect(() => {
    const q = queryParams.get('q') || '';
    const alreadyApplied = submittedPayload?.text || '';

    if (q && q !== alreadyApplied) {
      // URL has a search query that differs from current - apply it
      setSearchInput(q);
      setSubmittedPayload({
        ...(buildBaseFromFilters(filters) as SearchMembersByFieldsPayload),
        text: q,
        page: 1,
        limit,
        sortBy: toSortBy(orderBy),
        sortOrder: toSortOrder(orderDirection),
      });
    } else if (!submittedPayload && !q) {
      // No URL search and no payload yet - set default

      setSubmittedPayload(buildPayload());
    }
  }, [
    queryParams,
    filters,
    buildBaseFromFilters,
    limit,
    orderBy,
    orderDirection,
    submittedPayload,
    buildPayload,
  ]);

  const { membersSearch, isLoading, isFetching, refetch } = useSearchMembersByFieldsQuery(
    buildApiParamsFromPayload(submittedPayload),
  );
  const members: MemberSearchItem[] = useMemo(
    () => (membersSearch?.data && Array.isArray(membersSearch.data) ? membersSearch.data : []),
    [membersSearch],
  );
  const total = membersSearch?.total || 0;

  // Fetch impersonation data (account IDs + tags) for the current page.
  // Fetches team memberships per unique team + one tags call per tenant/realm group.
  const { impersonationMap, isLoadingImpersonation, isImpersonationError } =
    useMembersImpersonationQuery(members);

  const [isManuallyLoading, setIsManuallyLoading] = useState(false);
  useEffect(() => {
    if (!isLoading && !isFetching) {
      setIsManuallyLoading(false);
    }
  }, [isLoading, isFetching]);

  // Selection state helpers (selectedIdsState declared earlier for callback access)
  const allIdsOnPage = useMemo(() => new Set(members.map((m) => m.id)), [members]);
  const selectedIds = useMemo(() => new Set(selectedIdsState), [selectedIdsState]);
  const selectedCountOnPage = useMemo(
    () => Array.from(allIdsOnPage).filter((id) => selectedIds.has(id)).length,
    [allIdsOnPage, selectedIds],
  );
  const isAllSelectedOnPage = allIdsOnPage.size > 0 && selectedCountOnPage === allIdsOnPage.size;
  const isIndeterminateOnPage = selectedCountOnPage > 0 && selectedCountOnPage < allIdsOnPage.size;
  const hasSelectionOnPage = selectedCountOnPage > 0;

  const toggleSelectAllOnPage = (checked: boolean) => {
    setSelectedIdsState((prev) => {
      const next = new Set(prev);
      if (checked) {
        allIdsOnPage.forEach((id) => next.add(id));
      } else {
        allIdsOnPage.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const toggleRow = (rowKey: string) => {
    setSelectedIdsState((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const paginationProps = useMemo(
    () => ({
      count: total,
      page: pageNumber,
      rowsPerPage: limit,
      rowsPerPageOptions: [],
      onPageChange,
      indexBase: 1 as const,
    }),
    [limit, onPageChange, pageNumber, total],
  );
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.tenant) count++;
    if (filters.realmId) count++;
    if (filters.teamIds && filters.teamIds.length > 0) count++;
    // Only count status filter if it differs from the default (just "active")
    const isDefaultStatus = filters.statuses.length === 1 && filters.statuses[0] === 'active';
    if (!isDefaultStatus) count++;
    return count;
  }, [filters]);

  // Tenant/Realm selectors (shared styling + local filter behavior)
  const { openTenantDropdown } = useTenantDropdown();
  const { onSelectRealm } = useTenantRealmDropdownsFlow();
  const realmDropdownRef = useRef<HTMLDivElement>(null);
  const { tenantsHash, tenantOrder } = useTenants({ isOff: false });
  const selectedTenantIdForFilter = useMemo(() => {
    if (!filters.tenant) return null;
    const match = tenantOrder.find((id) => tenantsHash[id]?.tenantName === filters.tenant);
    return match || null;
  }, [filters.tenant, tenantOrder, tenantsHash]);

  const onTenantSelectorClick: React.MouseEventHandler<HTMLElement> = (e) => {
    openTenantDropdown({
      target: e.currentTarget,
      shouldSkipSetTenant: true,
      onSelect: ({ tenant }) => setFilters({ ...filters, tenant: tenant.tenantName, realmId: '' }),
    });
  };

  const onRealmSelectorClick: React.MouseEventHandler<HTMLElement> = (e) => {
    if (!selectedTenantIdForFilter) {
      // No tenant selected, cannot select realm
      return;
    }
    onSelectRealm({
      target: e.currentTarget,
      tenantId: selectedTenantIdForFilter,
      onSelect: ({ realm }) => setFilters({ ...filters, realmId: realm.realm_id }),
    });
  };

  return (
    <PageContainer>
      <Helmet title="Members" />

      <StandardPageHeader
        title="Members"
        subtitle="Search and manage member accounts across all teams"
        icon="Members"
        sx={{ mb: 6 }}
      />

      {/* Search and Quick Actions */}
      <MinimalCard $hoverEffect={false} sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <SearchInput
            fullWidth
            placeholder="Search by account ID, name, or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const searchText = searchInput.trim();
                setSearchInput(searchText);
                setIsManuallyLoading(true);
                setSelectedIdsState(new Set());
                handlePageChange({ newPageNumber: 1 });
                const mergedParams = Object.fromEntries(queryParams.entries());
                if (searchText) mergedParams.q = searchText;
                else delete (mergedParams as any).q;
                pushQueryParams(mergedParams);
                setSubmittedPayload({
                  ...buildBaseFromFilters(filters),
                  text: searchText || undefined,
                  page: 1,
                  limit,
                  sortBy: toSortBy(orderBy),
                  sortOrder: toSortOrder(orderDirection),
                });
              }
            }}
            slotProps={{
              input: {
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchInput('');
                        const entriesObj: Record<string, string> = Object.fromEntries(
                          queryParams.entries(),
                        );
                        const { q: _omit, ...rest } = entriesObj;
                        pushQueryParams(rest);
                      }}
                    >
                      <X size={16} />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            sx={{ flex: 1, minWidth: 300 }}
          />

          <CleanButton
            variant="contained"
            startIcon={<Search size={18} />}
            onClick={() => {
              const searchText = searchInput.trim();
              setSearchInput(searchText);
              setIsManuallyLoading(true);
              setSelectedIdsState(new Set());
              handlePageChange({ newPageNumber: 1 });
              const mergedParams = Object.fromEntries(queryParams.entries());
              if (searchText) mergedParams.q = searchText;
              else delete (mergedParams as any).q;
              pushQueryParams(mergedParams);
              setSubmittedPayload({
                ...buildBaseFromFilters(filters),
                text: searchText || undefined,
                page: 1,
                limit,
                sortBy: toSortBy(orderBy),
                sortOrder: toSortOrder(orderDirection),
              });
            }}
            disabled={isFetching}
          >
            Search
          </CleanButton>

          <CleanButton
            variant="outlined"
            startIcon={filtersExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            endIcon={
              activeFilterCount > 0 ? (
                <Box
                  sx={{
                    background: 'primary.main',
                    color: 'white',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                  }}
                >
                  {activeFilterCount}
                </Box>
              ) : undefined
            }
            onClick={() => setFiltersExpanded((v) => !v)}
          >
            Filters
          </CleanButton>

          <CleanButton variant="outlined" startIcon={<RefreshCw size={18} />} onClick={onReset}>
            Reset
          </CleanButton>
        </Box>
      </MinimalCard>

      {/* Expandable Filters */}
      <Collapse in={filtersExpanded}>
        <MinimalCard $hoverEffect={false} sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Filter size={20} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                }}
              >
                Advanced Filters
              </Typography>
            </Box>
            {activeFilterCount > 0 && (
              <CleanButton
                size="small"
                variant="text"
                startIcon={<X size={16} />}
                onClick={onReset}
              >
                Clear All
              </CleanButton>
            )}
          </Box>
          {/* Organization */}
          <Box
            sx={{
              padding: 3,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              mb: 3,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Building2 size={16} /> Organization
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              <TenantSelector
                onClick={onTenantSelectorClick}
                tenantId={selectedTenantIdForFilter}
              />
              <RealmSelector
                onClick={onRealmSelectorClick}
                realmId={filters.realmId || ''}
                ref={realmDropdownRef}
              />
            </Box>
          </Box>

          {/* Team IDs */}
          <Box
            sx={{
              padding: 3,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              mb: 3,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Tag size={16} /> Team IDs
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 1,
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Team ID"
                placeholder="Enter ID and press Enter"
                value={teamIdInput}
                onChange={(e) => setTeamIdInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddTeamIdChip();
                }}
              />
              <ButtonGroup variant="outlined" size="small">
                <Button
                  variant={filters.teamIdsMode === 'include' ? 'contained' : 'outlined'}
                  onClick={() => setFilters({ ...filters, teamIdsMode: 'include' })}
                >
                  Include
                </Button>
                <Button
                  variant={filters.teamIdsMode === 'exclude' ? 'contained' : 'outlined'}
                  onClick={() => setFilters({ ...filters, teamIdsMode: 'exclude' })}
                >
                  Exclude
                </Button>
              </ButtonGroup>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              {(filters.teamIds || []).map((id) => (
                <Chip key={id} color="primary" label={id} onDelete={() => removeTeamIdChip(id)} />
              ))}
            </Box>
          </Box>

          {/* Member Status */}
          <Box
            sx={{
              padding: 3,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Users size={16} /> Member Status
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              {(['active', 'deactivated', 'deleted'] as const).map((status) => (
                <FormControlLabel
                  key={status}
                  control={
                    <Checkbox
                      checked={filters.statuses.includes(status)}
                      onChange={(e) => {
                        setFilters((prev) => {
                          // Prevent unchecking the last remaining status
                          if (
                            !e.target.checked &&
                            prev.statuses.length === 1 &&
                            prev.statuses[0] === status
                          ) {
                            return prev;
                          }
                          return {
                            ...prev,
                            statuses: e.target.checked
                              ? [...prev.statuses, status]
                              : prev.statuses.filter((s) => s !== status),
                          };
                        });
                      }}
                      size="small"
                    />
                  }
                  label={status.charAt(0).toUpperCase() + status.slice(1)}
                />
              ))}
            </Box>
          </Box>
        </MinimalCard>
      </Collapse>

      <MinimalCard $hoverEffect={false}>
        <MembersV2Table
          members={members}
          isLoading={isLoading || isManuallyLoading}
          selectedIds={selectedIds}
          hasSelectionOnPage={hasSelectionOnPage}
          allOnPageSelected={isAllSelectedOnPage}
          isIndeterminateOnPage={isIndeterminateOnPage}
          onToggleRow={toggleRow}
          onToggleSelectAll={toggleSelectAllOnPage}
          paginationProps={paginationProps}
          orderBy={orderBy}
          orderDirection={orderDirection}
          onSort={onSort}
          impersonationMap={impersonationMap}
          isLoadingImpersonation={isLoadingImpersonation}
          isImpersonationError={isImpersonationError}
        />
      </MinimalCard>
    </PageContainer>
  );
};

export default MembersPage;
